import React, { useState, useEffect, useCallback, useRef } from 'react'
import Header from '../../components/Header/Header'
import { useNavigate } from 'react-router';
import { baseUrl } from '../../Urls';
import { FaEdit } from "react-icons/fa";
import * as XLSX from 'xlsx';
import '../../App.css';
import '../SalesOrder/salesorder.css';
import TermPicker from '../../components/TermPicker/TermPicker';
import { LuShrink } from "react-icons/lu";
import { IoExpand } from "react-icons/io5";
import ExpandableText from '../../components/ExpandableText/ExpandableText';

function SalesOrder() {
  const [salesList, setSalesList] = useState([]);
  const [filteredSales, setFilteredSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortOrder, setSortOrder] = useState('asc');
  const [sortColumn, setSortColumn] = useState('');
  const [selectedRow, setSelectedRow] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [filters, setFilters] = useState({
    sources: new Set(),
  });

  const navigate = useNavigate();

  const processSalesData = useCallback((data) => {
    return data.map(item => {
      if (item.assignedBy && item.assignedTo) {
        return item;
      } else {
        return {
          sNo: item.sNo,
          _id: item._id
        };
      }
    });
  }, []);

  const fetchSales = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${baseUrl}/api/sales`, {
        method: 'GET',
        credentials: 'include',
      });
      const data = await res.json();

      if (!res.ok) {
        console.error('Error fetching sales:', data);
        setSalesList([]);
        return;
      }
      const salesArray = Array.isArray(data) ? data : data.sales || [];
      const processed = processSalesData(salesArray); // ✅ filter assigned records only
      setSalesList(processed);
    } catch (err) {
      console.log('Error fetching sales:', err);
      setSalesList([]);
    } finally {
      setLoading(false);
    }
  }, [processSalesData]);

  useEffect(() => {
    fetchSales();
  }, [fetchSales]);

  useEffect(() => {
    setFilteredSales(salesList);
  }, [salesList]);

  const handleEdit = (sale) => {
    sessionStorage.setItem("lastEditedSalesOrderId", sale._id);
    navigate('/sales', { state: { sale, from: 'sales-order' } })
  };


  const handleSort = (column) => {
    if (column !== 'status') return;

    const orderMap = { won: 1, pipeline: 2, lose: 3 };

    const sorted = [...filteredSales].sort((a, b) => {
      const aVal = orderMap[a.status?.toLowerCase()] || 99;
      const bVal = orderMap[b.status?.toLowerCase()] || 99;
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });

    setFilteredSales(sorted);
    setSortOrder(sortOrder === 'asc' ? 'asc' : 'desc');
    setSortColumn(column);

    setTimeout(() => {
      if (tableContainerRef.current) {
        tableContainerRef.current.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    }, 0);
  }

  const tableContainerRef = useRef(null);
  const isEditingRef = useRef(false);

  useEffect(() => {
    if (!filteredSales.length || !tableContainerRef.current) return;

    const editedId = sessionStorage.getItem("lastEditedSalesOrderId");

    if (editedId) {
      // coming back from edit — scroll to the edited row instead of bottom
      isEditingRef.current = true;
      setTimeout(() => {
        const rowElement = document.getElementById(`row-${editedId}`);
        if (rowElement) {
          rowElement.scrollIntoView({ behavior: "smooth", block: "center" });
          rowElement.classList.add("highlight-row");
          setTimeout(() => rowElement.classList.remove("highlight-row"), 2000);
        }
        sessionStorage.removeItem("lastEditedSalesOrderId");
      }, 500); // allow DOM to render rows
    } else if (!isEditingRef.current) {
      // fresh load or normal navigation — scroll to bottom
      requestAnimationFrame(() => {
        const container = tableContainerRef.current;
        if (!container) return;
        container.scrollTop = container.scrollHeight;
      });
    }
  }, [filteredSales]);

  const columns = [
    { key: 'edit', label: 'Edit', isAction: true },
    { key: 'sNo', label: 'SNo' },
    { key: 'date', label: 'Enq Date' },
    { key: 'companyName', label: 'Company Name' },
    { key: 'contactPerson', label: 'Contact Person' },
    { key: 'phone', label: 'Phone' },
    { key: 'productName', label: 'Product Name' },
    { key: 'addressLine1', label: 'Address' },
    { key: 'tel', label: 'Telephone' },
    { key: 'email', label: 'Email' },
    { key: 'scope', label: 'Scope', render: (sale) => <ExpandableText text={sale.scope} maxLines={2} />, },
    { key: 'source', label: 'Source' },
    { key: 'attachments', label: 'Lead Attachments', isAttachment: true },
    { key: 'assignedBy', label: 'Assigned By' },
    { key: 'assignedTo', label: 'Assigned To' },
    { key: 'note', label: 'Note', render: (sale) => <ExpandableText text={sale.note} maxLines={2} />, },
    { key: 'estDate', label: 'EST Date', default: '-' },
    { key: 'quotationNo', label: 'Quotation No' },
    { key: 'estimator', label: 'Estimator' },
    { key: 'estAttachments', label: 'EST Sheet', isAttachment: true },
    { key: 'qutPdf', label: 'Quotation Proposal', isAttachment: true },
    { key: 'poStatus', label: 'Proposal Status', isStatus: true },


    { key: 'soDate', label: 'Follow Up-1' },
    { key: 'soNote', label: 'Note-1' },
    { key: 'soDateTwo', label: 'Follow Up-2' },
    { key: 'soNoteTwo', label: 'Note-2' },
    { key: 'soDateThree', label: 'Follow Up-3' },
    { key: 'soNoteThree', label: 'Note-3' },
    { key: 'status', label: 'Status' },
    { key: 'amount', label: 'Amount', isCurrency: true },
    { key: 'poAttachments', label: 'PO Attcahments Link', isAttachment: true },
    { key: 'reason', label: 'Reason' },
  ];

  const exportToExcel = () => {
    const dataToExport = filters.sources.size === 0 ? salesList : filteredSales;

    if (!dataToExport.length) {
      alert("No data to export!");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet([...filteredSales].map(sale => ({
      "SNo": sale.sNo,
      "Enq Date": sale.date,
      "Company Name": sale.companyName,
      "Product Name": sale.productName,
      "Address": sale.addressLine1,
      "Contact Person": sale.contactPerson,
      "Phone": sale.phone,
      "Telehone": sale.tel,
      "Email": sale.email,
      "Scope": sale.scope,
      "Source": sale.source,
      "Attachments": sale.attachments?.length ? sale.attachments.join(", ") : "-",

      "Assigned by": sale.assignedBy,
      "Assigned to": sale.assignedTo,
      "Note": sale.note,

      "EST Date": sale.estDate || "-",
      "Quotation No": sale.quotationNo,
      "Estimator": sale.estimator,
      "Est Sheet": sale.estAttachments?.length ? sale.estAttachments.join(", ") : "-",
      "Quotation Proposal": sale.qutPdf?.length ? sale.qutPdf.join(", ") : "-",
      "Proposal Status": sale.poStatus,

      "Follow-Up-1": sale.soDate || "-",
      "Note-1": sale.soNote || "-",
      "Follow-Up-2": sale.soDateTwo || "-",
      "Note-2": sale.soNoteTwo || "-",
      "Follow-Up-3": sale.soDateThree || "-",
      "Note-3": sale.soNoteThree || "-",
      "Status": sale.status,
      "Amount": sale.amount,
      "PO Attcahments Link": sale.poAttachments?.length ? sale.poAttachments.join(", ") : "-",
      "Reason": sale.reason,
    })));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "SalesOrders");

    const fileName = filters.sources.size === 0 ? "All_Sales.xlsx" : `Sales_${[...filters.sources].join("_")}.xlsx`;

    XLSX.writeFile(workbook, fileName);
  };
  const sourceOptions = [
    "Indiamart",
    "Website",
    "Mail",
    "Prev-client",
    "Distributor",
    "Other",];

  const handleDateFilter = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name === 'start' ? 'startDate' : 'endDate']: value }));
  };

  useEffect(() => {
    let filtered = [...salesList];

    if (filters.sources.size > 0) {
      filtered = filtered.filter(
        sale => filters.sources.has(sale.source?.toLowerCase())
      );
    }

    if (filters.startDate && filters.endDate) {
      const start = new Date(filters.startDate);
      const end = new Date(filters.endDate);

      filtered = filtered.filter(sale => {
        if (!sale.date) return false;
        const saleDate = new Date(sale.date);
        return saleDate >= start && saleDate <= end;
      });
    }

    setFilteredSales(filtered);
  }, [filters.sources, filters.startDate, filters.endDate, salesList]);

  return (
    <>
      <Header salesList={salesList} />

      <div className='container sales-order-container' style={{ marginTop: "100px" }}>
        <h2 className='so-heading' style={{ color: "white", fontFamily: "italic" }}>Sales Order Page</h2>
        <span
          className='toggle-icon'
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          style={{ width: "40px" }}
        >
          {isSidebarOpen ? <IoExpand /> : <LuShrink />}
        </span>
        <div className='row mt-3'>
          {isSidebarOpen && (
            <div className='col-lg-3 col-md-4 mb-2 order-2 order-md-2 order-lg-1 so-sidesec'>
              <button className='exp-btn' type="button" onClick={exportToExcel}>Export</button>
              <h6 className='status-title'>Filter by Source</h6>
              {sourceOptions.map((src) => (
                <div className="form-check" style={{ color: "white" }} key={src}>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`source-${src}`}
                    checked={filters.sources.has(src.toLowerCase())}
                    onChange={(e) => {
                      const updated = new Set();
                      if (e.target.checked) {
                        updated.add(src.toLowerCase())
                      }
                      setFilters((prev) => ({ ...prev, sources: updated }));
                    }}
                  />
                  <label className="form-check-label" htmlFor={`source-${src}`}>
                    {src}
                  </label>
                </div>
              ))}
              <TermPicker
                startName="start"
                endName="end"
                onChange={handleDateFilter}
              />
            </div>
          )}

          <div className={`table-container-so ${isSidebarOpen ? 'col-lg-9 col-md-8' : 'col-12'} order-1 order-md-1 order-lg-2 p-0`} ref={tableContainerRef}>
            {loading ? (
              <div className="spinner-container">
                <div className="spinner"></div>
                <p>Loading SO data...</p>
              </div>
            ) : (
              <table className="table table-bordered so-table table-hover" style={{ fontSize: "14px" }}>
                <thead>
                  <tr>
                    {columns.map((col) => (
                      <th
                        key={col.key}
                        onClick={() => handleSort(col.key)}
                        style={{
                          cursor: col.key === 'status' ? 'pointer' : 'default',
                        }}
                      >
                        {col.label}
                        {col.key === sortColumn && (sortOrder === 'asc' ? ' 🔼' : ' 🔽')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredSales.length === 0 ? (
                    <tr>
                      <td colSpan="25" className="text-left text-dark">
                        No data found
                      </td>
                    </tr>
                  ) : (
                    filteredSales.map((sale) => (
                      <tr
                        id={`row-${sale._id}`}
                        key={sale._id}
                        onClick={() =>
                          setSelectedRow(selectedRow === sale._id ? null : sale._id)
                        }
                        className={selectedRow === sale._id ? 'highlight-row' : ''}
                        style={{ cursor: "pointer" }}
                      >
                        {columns.map((col) => {
                          if (col.isAction) {
                            return (
                              <td key={col.key}>
                                <FaEdit
                                  style={{ marginLeft: "5px", color: "gray", cursor: "pointer" }}
                                  onClick={() => handleEdit(sale)}
                                />
                              </td>
                            );
                          }
                          if (col.isAttachment) {
                            return (
                              <td key={col.key}>
                                {sale[col.key]?.map((link, idx) => (
                                  <div key={idx}>
                                    <a href={link} target="_blank" rel="noopener noreferrer">
                                      View File {idx + 1}
                                    </a>
                                  </div>
                                ))}
                              </td>
                            );
                          }
                          if (col.isCurrency) {
                            return (
                              <td key={col.key}>
                                {sale[col.key] ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(sale[col.key]) : ''}
                              </td>
                            );
                          }
                          if (col.isStatus) {
                            let displayStatus = sale.poStatus;
                            if (!sale.poStatus || sale.poStatus === 'InProgress' || sale.poStatus === 'pending') {
                              if (sale.qutPdf && sale.qutPdf.length > 0) {
                                displayStatus = 'Pending';
                              } else {
                                displayStatus = 'InProgress';
                              }
                            }

                            const badgeClass =
                              displayStatus === 'approved'
                                ? 'bg-success'
                                : displayStatus === 'rejected'
                                  ? 'bg-danger'
                                  : 'bg-warning text-dark';

                            return (
                              <td key={col.key}>
                                <span className={`badge ${badgeClass}`}>
                                  {displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)}
                                </span>
                              </td>
                            );
                          }
                          if (col.render) {
                            return <td key={col.key}>{col.render(sale)}</td>;
                          }
                          return <td key={col.key}>{sale[col.key] ?? col.default ?? ''}</td>;
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div >
    </>
  )
}

export default SalesOrder